import { FunctionComponent, useMemo } from 'react';
import styles from './UnitSelector.module.scss';
import { RoomUnit } from '@spike/room-model';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faPaw } from '@fortawesome/pro-light-svg-icons';
import GenericSelect, {
    GenericSelectOption
} from 'components/CreateBooking/Transportation/UI/GenericSelect/GenericSelect';

const UNASSIGNED_OPTION = { id: -1, label: 'Unassigned' } as const;

export type UnitBadge = {
    text: string;
    showWarningIcon?: boolean;
};

interface UnitSelectorProps {
    units: RoomUnit[];
    onSelect: (unit: number | undefined) => void;
    className?: string;
    label?: string | JSX.Element;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    initialUnitId?: number;
    getUnitBadge?: (unit: RoomUnit) => UnitBadge | undefined;
}

export const UnitSelector: FunctionComponent<UnitSelectorProps> = ({
    units,
    onSelect,
    className,
    placeholder = 'Select unit',
    disabled,
    initialUnitId,
    getUnitBadge
}) => {
    const options = units as Array<{ id: number; name: string; active?: boolean }>;
    const optionsWithUnassigned = [UNASSIGNED_OPTION, ...options];

    const handleSelect = (option: GenericSelectOption) => {
        if (option.id === UNASSIGNED_OPTION.id) {
            onSelect(undefined);
        } else {
            onSelect(Number(option.id));
        }
    };

    const renderBadge = (badge: UnitBadge) => (
        <span className={styles.badge}>
            <FontAwesomeIcon
                icon={badge.showWarningIcon ? faExclamationTriangle : faPaw}
                className={styles.badgeIcon}
            />
            <span className={styles.badgeText}>{badge.text}</span>
        </span>
    );

    const mappedOptions: GenericSelectOption[] = useMemo(
        () =>
            optionsWithUnassigned.map(option => {
                if ('label' in option) {
                    return option;
                }

                const badge = getUnitBadge?.(option as RoomUnit);

                return {
                    id: option.id,
                    label: option.name,
                    disabled: option.active === false,
                    rightLabel: badge ? renderBadge(badge) : undefined
                };
            }),
        [optionsWithUnassigned, getUnitBadge]
    );

    const selectedUnit = useMemo(
        () => mappedOptions.find(option => option.id === (initialUnitId ?? UNASSIGNED_OPTION.id)) ?? UNASSIGNED_OPTION,
        [mappedOptions, initialUnitId]
    );

    return (
        <div className={styles.container}>
            <GenericSelect
                options={mappedOptions}
                selected={selectedUnit}
                onSelect={handleSelect}
                placeholder={placeholder}
                className={className}
                showRightLabelOnSelected
                disabled={disabled}
                inlineRightLabel
            />
        </div>
    );
};

export default UnitSelector;
